import { defineStore } from 'pinia';
import type { Proposal } from '~/types/models';
import { getRequiredAuthHeaders } from '~/lib/apiAuth';
import { createTenantDocument, deleteTenantDocument, fetchTenantCollection, updateTenantDocument, type TenantDocumentPayload } from '~/lib/tenantCollection';
import { generatePublicAccessToken } from '~/lib/publicAccessTokens';
import { toMillis } from '~/lib/timestamps';
import { stripUndefined } from '~/lib/stripUndefined';
import { hasActiveProposalPublicLink } from '~/lib/proposalPublicLink';
import { ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import { waitForResumableUpload } from '~/lib/resumableUpload';
import { buildPublicProposalLink, resolveTenantOrigin } from '~/lib/tenantLinks';

export type ProposalInput = Pick<Proposal, 'clientId' | 'projectId' | 'title' | 'sections' | 'options' | 'contentMode' | 'publicLinkExpiryDays'>;

export function createProposalDraft(): ProposalInput {
    return {
        clientId: '', projectId: '', title: 'Project proposal', contentMode: 'composed', publicLinkExpiryDays: 30,
        sections: [
            { id: crypto.randomUUID(), heading: 'The opportunity', body: '' },
            { id: crypto.randomUUID(), heading: 'Recommended approach', body: '' },
            { id: crypto.randomUUID(), heading: 'Deliverables', body: '' },
            { id: crypto.randomUUID(), heading: 'Timeline and next steps', body: '' },
        ],
        options: [{ id: crypto.randomUUID(), title: 'Recommended investment', description: '', amount: 0 }],
    };
}

export const useProposalsStore = defineStore('proposals', {
    state: () => ({ proposals: [] as Proposal[], loading: false, saving: false, error: null as string | null }),
    actions: {
        tenantId() { const id = useTenantStore().currentTenantId; if (!id) throw new Error('No tenant selected.'); return id; },
        publicOrigin() {
            const config = useRuntimeConfig();
            return resolveTenantOrigin({ tenant: useTenantStore().currentTenant, appDomain: config.public.appDomain, fallbackOrigin: window.location.origin });
        },
        buildPublicLink(proposal: Pick<Proposal, 'id' | 'publicAccessToken'>): string | null {
            const token = String(proposal.publicAccessToken || '').trim();
            if (!token) return null;
            return buildPublicProposalLink({ origin: this.publicOrigin(), token, tenantId: this.tenantId(), proposalId: proposal.id });
        },
        sort(items: Proposal[]) { return [...items].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt)); },
        async fetchAll() {
            this.loading = true; this.error = null;
            try { this.proposals = this.sort(await fetchTenantCollection<Proposal>(useNuxtApp().$db, this.tenantId(), 'proposals')); }
            catch (error: any) { this.error = error?.message || 'Failed to load proposals.'; }
            finally { this.loading = false; }
        },
        async create(input: ProposalInput) {
            if (!input.clientId) throw new Error('Choose the client before saving a proposal.');
            this.saving = true;
            try {
                const payload = stripUndefined({ ...input, projectId: input.projectId || undefined, status: 'draft' as const, tenantId: this.tenantId(), createdAt: new Date(), updatedAt: new Date() });
                const id = await createTenantDocument(useNuxtApp().$db, this.tenantId(), 'proposals', payload as TenantDocumentPayload);
                this.proposals = this.sort([{ ...payload, id } as Proposal, ...this.proposals]); return id;
            } finally { this.saving = false; }
        },
        async save(id: string, input: ProposalInput) {
            const existing = this.proposals.find(item => item.id === id);
            if (!existing || existing.status !== 'draft') throw new Error('Only draft proposals can be edited.');
            this.saving = true;
            try { const patch = stripUndefined({ ...input, projectId: input.projectId || undefined, updatedAt: new Date() }); await updateTenantDocument(useNuxtApp().$db, this.tenantId(), 'proposals', id, patch as TenantDocumentPayload); this.proposals = this.proposals.map(p => p.id === id ? { ...p, ...patch } as Proposal : p); }
            finally { this.saving = false; }
        },
        _requireReadyToShare(proposal: Proposal) {
            if (proposal.contentMode === 'uploaded' && !proposal.document) throw new Error('Upload the proposal PDF before sharing.');
            if (proposal.contentMode !== 'uploaded' && !proposal.sections.some(section => section.body.trim())) throw new Error('Add the proposal content before sharing.');
            if (proposal.contentMode !== 'uploaded' && !proposal.options.length) throw new Error('Add an investment option before sharing.');
        },
        async preparePublicLink(id: string) {
            const proposal = this.proposals.find(item => item.id === id); if (!proposal) throw new Error('Proposal not found.');
            this._requireReadyToShare(proposal);
            if (hasActiveProposalPublicLink(proposal)) return proposal.publicAccessToken!;
            const now = new Date();
            const expiryDays = Math.max(1, Math.min(365, Number(proposal.publicLinkExpiryDays || 30)));
            const patch = {
                publicAccessToken: generatePublicAccessToken(),
                publicSharedAt: now,
                publicLinkExpiresAt: new Date(now.getTime() + expiryDays * 24 * 60 * 60 * 1000),
                updatedAt: now,
            };
            await updateTenantDocument(useNuxtApp().$db, this.tenantId(), 'proposals', id, patch);
            this.proposals = this.proposals.map(p => p.id === id ? { ...p, ...patch } : p);
            return patch.publicAccessToken;
        },
        async markSent(id: string, deliveryMethod: NonNullable<Proposal['deliveryMethod']>, deliveryMessage?: string) {
            const proposal = this.proposals.find(item => item.id === id); if (!proposal) throw new Error('Proposal not found.');
            if (proposal.status !== 'draft' && proposal.status !== 'sent') throw new Error('Only pending proposals can be shared.');
            this._requireReadyToShare(proposal);
            const now = new Date();
            const expiryDays = Math.max(1, Math.min(365, Number(proposal.publicLinkExpiryDays || 30)));
            const linkIsActive = hasActiveProposalPublicLink(proposal, now.getTime());
            const publicSharedAt = linkIsActive ? proposal.publicSharedAt : now;
            const publicLinkExpiresAt = linkIsActive
                ? proposal.publicLinkExpiresAt || new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
                : new Date(now.getTime() + expiryDays * 24 * 60 * 60 * 1000);
            const patch = {
                status: 'sent' as const,
                publicAccessToken: linkIsActive ? proposal.publicAccessToken! : generatePublicAccessToken(),
                publicSharedAt,
                publicLinkExpiresAt,
                deliveryMethod,
                ...(String(deliveryMessage || '').trim() ? { deliveryMessage: String(deliveryMessage).trim().slice(0, 4_000) } : {}),
                ...(deliveryMethod === 'email' ? { emailSentAt: now } : {}),
                updatedAt: now,
            };
            await updateTenantDocument(useNuxtApp().$db, this.tenantId(), 'proposals', id, patch);
            this.proposals = this.proposals.map(p => p.id === id ? { ...p, ...patch } : p);
            return patch.publicAccessToken;
        },
        async share(id: string, deliveryMethod: NonNullable<Proposal['deliveryMethod']> = 'public_link') {
            const token = await this.preparePublicLink(id);
            const proposal = this.proposals.find(item => item.id === id);
            if (proposal?.status === 'draft' || proposal?.status === 'sent') return this.markSent(id, deliveryMethod);
            return token;
        },
        async recordPortalApproval(id: string, approvalId: string) {
            const proposal = this.proposals.find(item => item.id === id); if (!proposal) throw new Error('Proposal not found.');
            const patch = { portalApprovalId: approvalId, deliveryMethod: 'portal' as const, updatedAt: new Date() };
            await updateTenantDocument(useNuxtApp().$db, this.tenantId(), 'proposals', id, patch);
            this.proposals = this.proposals.map(item => item.id === id ? { ...item, ...patch } : item);
        },
        async deleteProposal(id: string) {
            const proposal = this.proposals.find(item => item.id === id); if (!proposal) throw new Error('Proposal not found.');
            if (proposal.status !== 'draft') throw new Error('Only draft proposals can be deleted.');
            await deleteTenantDocument(useNuxtApp().$db, this.tenantId(), 'proposals', id);
            this.proposals = this.proposals.filter(item => item.id !== id);
        },
        async uploadDocument(id: string, file: File, onProgress?: (progress: number) => void) {
            if ((file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) || file.size > 20 * 1024 * 1024) throw new Error('Choose a PDF up to 20 MB.');
            if (await file.slice(0, 5).text() !== '%PDF-') throw new Error('The selected file is not a valid PDF.');
            const { $storage, $auth } = useNuxtApp();
            const userId = $auth.currentUser?.uid;
            if (!$storage || !userId) throw new Error('Sign in again before uploading the proposal PDF.');
            const tenantId = this.tenantId();
            const { ticketId, storagePath } = await $fetch<{ ticketId: string, storagePath: string }>(`/api/proposals/${encodeURIComponent(id)}/upload-session`, {
                method: 'POST', query: { tenantId }, headers: await getRequiredAuthHeaders(),
            });
            const task = uploadBytesResumable(storageRef($storage, storagePath), file, {
                contentType: 'application/pdf',
                customMetadata: { uploadedBy: userId, ticketId },
            });
            await waitForResumableUpload(task, { onProgress });
            const result = await $fetch<{ document: NonNullable<Proposal['document']> }>(`/api/proposals/${encodeURIComponent(id)}/document`, {
                method: 'POST', query: { tenantId }, headers: await getRequiredAuthHeaders(), body: { storagePath, fileName: file.name, ticketId },
            });
            this.proposals = this.proposals.map(proposal => proposal.id === id ? { ...proposal, contentMode: 'uploaded', document: result.document } : proposal);
            return result.document;
        },
        async uploadSupportingDocument(id: string, file: File, onProgress?: (progress: number) => void) {
            if ((file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) || file.size > 20 * 1024 * 1024) throw new Error('Choose a PDF up to 20 MB.');
            if (await file.slice(0, 5).text() !== '%PDF-') throw new Error('The selected file is not a valid PDF.');
            const { $storage, $auth } = useNuxtApp(); const userId = $auth.currentUser?.uid;
            if (!$storage || !userId) throw new Error('Sign in again before uploading the supporting document.');
            const tenantId = this.tenantId();
            const { ticketId, storagePath } = await $fetch<{ ticketId: string, storagePath: string }>(`/api/proposals/${encodeURIComponent(id)}/upload-session`, { method: 'POST', query: { tenantId, supporting: '1' }, headers: await getRequiredAuthHeaders() });
            const task = uploadBytesResumable(storageRef($storage, storagePath), file, { contentType: 'application/pdf', customMetadata: { uploadedBy: userId, ticketId } });
            await waitForResumableUpload(task, { onProgress });
            const result = await $fetch<{ document: NonNullable<Proposal['supportingDocuments']>[number] }>(`/api/proposals/${encodeURIComponent(id)}/document`, { method: 'POST', query: { tenantId }, headers: await getRequiredAuthHeaders(), body: { storagePath, fileName: file.name, ticketId, supporting: true } });
            this.proposals = this.proposals.map(proposal => proposal.id === id ? { ...proposal, supportingDocuments: [...(proposal.supportingDocuments || []), result.document] } : proposal);
            return result.document;
        },
    },
});
